import { Component, Inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';

export interface LpoTemplateData {
  companyName: string;
  companyDetails: string;
  phoneNumber: string;
  telephoneNumber: string; // Added telephone
  email: string; // Added email
  lpoHeader: string; // Static TRN or similar
  lpoTerms: string;
  lpoFooter: string;
  logoUrl?: string; // Kept but unused in new UI
  logoBase64?: string; // PRE-FETCHED or passed in
  signatureUrl?: string; // Restored for data passing
  signatureBase64?: string; // Add base64 support
}

@Component({
  selector: 'app-lpo-template-dialog',
  templateUrl: './lpo-template-dialog.component.html',
  styleUrls: ['./lpo-template-dialog.component.scss'],
})
export class LpoTemplateDialogComponent implements OnInit {
  @ViewChild('sigCanvas') sigCanvas!: ElementRef<HTMLCanvasElement>;
  private ctx!: CanvasRenderingContext2D;
  private isDrawing = false;
  
  today = new Date();
  isEdit = false;
  selectedTabIndex = 0;

  constructor(
    public dialogRef: MatDialogRef<LpoTemplateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LpoTemplateData,
    private ux: UxserviceService
  ) {}

  ngOnInit(): void {
    // Determine if this is an Edit or Create action
    // If we have a header/terms/footer pre-filled, it's likely an edit (or at least a copy).
    // For a cleaner check, we could pass a flag, but checking content is a good heuristic for now.
    // Or we can check if the 'name' property suggests an existing template, but the data interface might not carry 'id'.
    // A simple check: if any visual field has content, assume edit/copy, else create.
    // Actually, distinct emptiness checks are better.

    this.isEdit = !!(
      this.data.lpoHeader ||
      this.data.lpoTerms ||
      this.data.lpoFooter
    );

    // Ensure all fields are initialized to avoid null issues
    this.data.companyName = this.data.companyName || '';
    this.data.companyDetails = this.data.companyDetails || '';
    this.data.phoneNumber = this.data.phoneNumber || '';
    this.data.telephoneNumber = this.data.telephoneNumber || '';
    this.data.email = this.data.email || '';
    this.data.lpoHeader = this.data.lpoHeader || '';
    this.data.lpoTerms = this.data.lpoTerms || '';
    this.data.lpoFooter = this.data.lpoFooter || '';
  }

  // ==========================================
  // SIGNATURE LOGIC (Instant Save to Storage)
  // ==========================================
  triggerSigInput() {
    document.getElementById('lpoTemplateSigInput')?.click();
  }

  async onSigSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      try {
        this.ux.showOverlay('Uploading Signature...');
        
        // Convert to Base64 for instant PDF use
        const base64 = await this.fileToBase64(file);

        const storage = getStorage();
        const path = `template_signatures/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, path);
        const uploadResult = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(uploadResult.ref);
        
        this.data.signatureUrl = url;
        this.data.signatureBase64 = base64; // Added base64
        this.ux.showSuccess('Signature uploaded!');
      } catch (error) {
        console.error('Error uploading signature:', error);
        this.ux.showError('Error uploading signature');
      } finally {
        this.ux.instantCloseOverlay();
      }
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  initCanvas() {
    if (this.sigCanvas) {
      const canvas = this.sigCanvas.nativeElement;
      this.ctx = canvas.getContext('2d')!;
      this.ctx.lineWidth = 2;
      this.ctx.lineCap = 'round';
      this.ctx.strokeStyle = '#000000';
    }
  }

  startDrawing(event: MouseEvent) {
    if (!this.ctx) this.initCanvas();
    this.isDrawing = true;
    this.ctx.beginPath();
    this.ctx.moveTo(event.offsetX, event.offsetY);
  }

  draw(event: MouseEvent) {
    if (!this.isDrawing) return;
    this.ctx.lineTo(event.offsetX, event.offsetY);
    this.ctx.stroke();
  }

  stopDrawing() {
    this.isDrawing = false;
    this.ctx.closePath();
  }

  clearCanvas() {
    if (this.sigCanvas) {
      const canvas = this.sigCanvas.nativeElement;
      this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  async saveCanvasSignature() {
    if (!this.sigCanvas) return;
    const canvas = this.sigCanvas.nativeElement;

    this.ux.showOverlay('Saving Signature...');
    
    // Get base64 directly from canvas
    const base64 = canvas.toDataURL('image/png');

    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          const storage = getStorage();
          const path = `template_signatures/drawn_${Date.now()}.png`;
          const storageRef = ref(storage, path);
          const uploadResult = await uploadBytes(storageRef, blob);
          const url = await getDownloadURL(uploadResult.ref);
          this.data.signatureUrl = url;
          this.data.signatureBase64 = base64; // Added base64
          this.ux.showSuccess('Signature saved!');
        } catch (error) {
          console.error('Error saving drawn signature:', error);
          this.ux.showError('Error saving signature');
        } finally {
          this.ux.instantCloseOverlay();
        }
      }
    }, 'image/png');
  }

  save() {
    this.dialogRef.close(this.data);
  }

  close() {
    this.dialogRef.close();
  }
}
